import React, { useCallback } from 'react';
import Modal from '../helpers/Modal';
import { BuildData } from '../../types';
import { useCommunityBuilds } from '../../hooks/modules/useCommunityBuilds';
import { Loader2, Download, User } from 'lucide-react';
import { formatDate } from '../../utils/formatDate';

interface CommunityBuildsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onLoadBuild: (build: BuildData) => void;
}

const CommunityBuildsModal: React.FC<CommunityBuildsModalProps> = ({ isOpen, onClose, onLoadBuild }) => {
    const { builds, loading, error } = useCommunityBuilds();

    const handleLoad = useCallback((build: BuildData) => {
        onLoadBuild(build);
        onClose();
    }, [onLoadBuild, onClose]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Community Builds" size="4xl">
            <div className="p-4 bg-slate-800 rounded-lg min-h-[50vh]">
                <p className="text-slate-400 mb-6 flex items-center gap-2">
                    <User className="text-sworn-gold w-5 h-5"/>
                    Hand-picked builds from the Sworn community.
                </p>

                {loading ? (
                    <div className="flex justify-center items-center h-40">
                        <Loader2 className="w-8 h-8 animate-spin text-sworn-gold" />
                    </div>
                ) : error ? (
                    <div className="text-red-500 text-center p-4 border border-red-500/50 rounded bg-red-900/20">
                        {error}
                    </div>
                ) : builds.length === 0 ? (
                    <div className="text-center text-slate-500 py-10">
                        No community builds available yet. Check back soon!
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {builds.map((build) => (
                            <div 
                                key={build.id} 
                                className="bg-slate-700/50 hover:bg-slate-700 border border-slate-600 hover:border-sworn-gold transition-all duration-200 rounded-lg p-4 group cursor-pointer"
                                onClick={() => handleLoad(build)}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <h3 className="font-bold text-lg text-white group-hover:text-sworn-gold transition-colors">{build.name}</h3>
                                        {build.author && build.author !== 'Unknown' && (
                                            <p className="text-xs text-[#e6a84f] italic mt-1 font-serif">by {build.author}</p>
                                        )}
                                    </div>
                                    <span className="text-xs text-slate-400 px-2 py-1 bg-slate-800 rounded whitespace-nowrap">{formatDate(build.updatedAt)}</span>
                                </div>
                                
                                <div className="text-sm text-slate-300 grid grid-cols-2 gap-y-1 gap-x-4 mb-4 mt-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-1 h-1 bg-sworn-gold rounded-full"></div>
                                        <span className="capitalize">{build.knight}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-1 h-1 bg-sworn-gold rounded-full"></div>
                                        <span className="capitalize">{build.weapon?.replace(/_/g, ' ')}</span>
                                    </div>
                                </div>

                                <button 
                                    className="w-full mt-2 bg-blue-600 hover:bg-blue-500 text-white py-2 rounded font-medium flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <Download className="w-4 h-4" /> Load build
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default CommunityBuildsModal;
